import { firstValueFrom, timeout } from 'rxjs';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { ProfileV2ApiService } from '@et/api';
import { applyPublicAuthProviderFromProbe } from '@et/guards';
import type { EscrowtabOidcApp } from '../injection/escrowtab-oidc-app.token';
import { escrowtabOAuthInitializerFactory } from './oauth.initializer';

export const NATIVE_PUBLIC_AUTH_PROBE_TIMEOUT_MS = 5000;

/**
 * Reads Profile `GET /public/auth` and stores the advertised provider for
 * iOS/Android so `useBetterAuthForCurrentPlatform()` is correct before OIDC
 * / MSAL bootstrap. Failures keep `environment.useBetterAuthNative`.
 */
export async function probeAndApplyNativePublicAuth(
  profileApi: ProfileV2ApiService,
  timeoutMs = NATIVE_PUBLIC_AUTH_PROBE_TIMEOUT_MS,
): Promise<void> {
  try {
    const result = await firstValueFrom(
      profileApi.getPublicAuth().pipe(timeout(timeoutMs)),
    );
    applyPublicAuthProviderFromProbe(result?.auth);
  } catch {
    // Probe is best-effort. A rejected APP_INITIALIZER would white-screen
    // the native shell; the build-time native flag remains the fallback.
  }
}

/**
 * Native APP_INITIALIZER: apply the public-auth flag, then run OIDC setup
 * so discovery/tryLogin see the same provider the login screen will use.
 */
export function nativeAuthBootstrapFactory(
  oauthService: OAuthService,
  app: EscrowtabOidcApp,
  storage: OAuthStorage,
  profileApi: ProfileV2ApiService,
): () => Promise<unknown> {
  const runOauth = escrowtabOAuthInitializerFactory(oauthService, app, storage);
  return () => probeAndApplyNativePublicAuth(profileApi).then(() => runOauth());
}
